import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import {
  DOWNLOAD_URL,
  MEMBER_DOWNLOAD_URL,
  REPO_URL,
  foundryPages,
  frameRows,
  pageBySlug,
} from './foundryData';
import { NetworkStation } from './components/NetworkStation';

const FoundryProgressLab = lazy(() => import('./components/FoundryProgressLab').then((module) => ({ default: module.FoundryProgressLab })));

type Props = { onLaunchDemo: () => void };

const routePrefix = '/foundry/';

function routeFromLocation() {
  const path = window.location.pathname.replace(/\/+$/, '');
  return path.startsWith(routePrefix) ? path.slice(routePrefix.length) : '';
}

function maxFrameCount() {
  return Math.max(...frameRows.map(row => row.compiled));
}

export default function FoundrySite({ onLaunchDemo }: Props) {
  const [route, setRoute] = useState(routeFromLocation);
  const activePage = useMemo(() => pageBySlug(route), [route]);

  useEffect(() => {
    const onPopState = () => setRoute(routeFromLocation());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    document.title = activePage
      ? `${activePage.title} — L34D Fighter Foundry`
      : 'L34D Fighter Foundry — The Smackers production workspace';
  }, [activePage]);

  function navigate(slug = '') {
    const nextPath = slug ? `${routePrefix}${slug}` : '/';
    window.history.pushState(null, '', nextPath);
    setRoute(slug);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  return (
    <div className="foundry-site">
      <header className="foundry-header">
        <div className="foundry-brand-cluster">
          <a className="foundry-hub-mark" href="https://l34d.ca" aria-label="Fighter Foundry — visit the L34D Software hub"><img src="/fighter-foundry-icon-v2.png" alt="Fighter Foundry angular mech-core mark" /></a>
          <button className="foundry-brand foundry-brand--button" type="button" onClick={() => navigate()} aria-label="Fighter Foundry home">
            <span><strong>Fighter Foundry</strong><small>Game production workspace</small></span>
          </button>
        </div>
        <nav aria-label="Fighter Foundry navigation">
          <a href="https://auth.l34d.ca/account/licenses">Releases</a>
          <button className="foundry-nav-link" type="button" onClick={() => navigate('pipeline')}>Pipeline</button>
          <button className="foundry-nav-link" type="button" onClick={() => navigate('smackers-progress')}>Progress</button>
          <a href={REPO_URL} target="_blank" rel="noreferrer">Repository</a>
          <button type="button" onClick={onLaunchDemo}>Launch demo</button>
        </nav>
      </header>

      <main>
        {activePage
          ? <DetailPage page={activePage} navigate={navigate} onLaunchDemo={onLaunchDemo} />
          : <HomePage navigate={navigate} onLaunchDemo={onLaunchDemo} />}
      </main>

      <NetworkStation />

      <footer className="foundry-footer">
        <span>© 2026 L34D Software · Fighter Foundry</span>
        <nav aria-label="Fighter Foundry footer">
          <button type="button" onClick={() => navigate()}>Foundry home</button>
          <a href={REPO_URL} target="_blank" rel="noopener noreferrer">Private repository</a>
          <a href="https://www.patreon.com/c/l34d/membership" target="_blank" rel="noopener noreferrer">Membership</a>
        </nav>
      </footer>
    </div>
  );
}

function HomePage({ navigate, onLaunchDemo }: { navigate: (slug?: string) => void; onLaunchDemo: () => void }) {
  const maximum = maxFrameCount();
  return (
    <>
      <section className="foundry-hero foundry-hero--hub">
        <div className="foundry-hero__art" aria-hidden="true" />
        <div className="foundry-hero__copy">
          <p className="foundry-kicker">L34D SOFTWARE · ACTIVE PRODUCTION WORKSPACE</p>
          <h1>Build the fighter.<br />Prove the pipeline.</h1>
          <p>Fighter Foundry’s production droid oversees the character harness, sprite processing, Ikemen adaptation and release evidence behind <em>The Smackers</em>. The browser cabinet is playable proof; these pages show how the work is actually made.</p>
          <div className="foundry-actions">
            <button type="button" onClick={onLaunchDemo}>Launch Smackers alpha</button>
            <a href={MEMBER_DOWNLOAD_URL}>Tester build · members</a>
            <a href={REPO_URL} target="_blank" rel="noreferrer">Private repository</a>
          </div>
          <dl className="foundry-status">
            <div><dt>Project</dt><dd>Active private alpha</dd></div>
            <div><dt>Runtime</dt><dd>Ikemen GO + browser</dd></div>
            <div><dt>Release</dt><dd>Foundry v0.0.1</dd></div>
          </dl>
        </div>
      </section>

      <section className="foundry-index" aria-labelledby="foundry-index-title">
        <div className="foundry-section-heading">
          <p>Engineering field notes</p>
          <h2 id="foundry-index-title">Six routes through the work</h2>
          <span>Start with the full production chain, or jump directly to the runtime, QA, progress or release owner.</span>
        </div>
        <div className="foundry-index__grid">
          {foundryPages.map(page => (
            <button key={page.slug} type="button" onClick={() => navigate(page.slug)}>
              <span>{page.number}</span>
              <div><strong>{page.title}</strong><small>{page.summary}</small></div>
              <b aria-hidden="true">→</b>
            </button>
          ))}
        </div>
      </section>

      <section className="foundry-workbench">
        <figure>
          <img src="/assets/foundry/pipeline-workbench.png" alt="Illustrated Fighter Foundry sprite production pipeline" />
          <figcaption>Pipeline workbench · source, normalization, harness, validation</figcaption>
        </figure>
        <div>
          <p className="foundry-kicker">THE REAL OWNER PATH</p>
          <h2>Reference in. Verified runtime out.</h2>
          <p>Approved source frames are normalized once, then consumed by character builders, contact-sheet QA, browser previews and the Windows runtime. That keeps release artifacts traceable to the exact inputs and scripts that made them.</p>
          <ol className="foundry-mini-pipeline">
            {['Define frame policy', 'Normalize transparent drawings', 'Build SFF and AIR data', 'Integrate states and stages', 'Verify and package'].map((item, index) => (
              <li key={item}><span>{String(index + 1).padStart(2, '0')}</span>{item}</li>
            ))}
          </ol>
          <button className="foundry-text-action" type="button" onClick={() => navigate('pipeline')}>Read the full pipeline →</button>
        </div>
      </section>

      <section className="foundry-production-floor" aria-labelledby="production-floor-title">
        <div className="foundry-section-heading">
          <p>Production floor</p>
          <h2 id="production-floor-title">The frames, passes, and proof behind a fighter</h2>
          <span>These are working artifacts from the current character pipeline: transparent source poses, contact sheets, timed animation previews, runtime integration, and release QA.</span>
        </div>
        <div className="foundry-production-floor__lead">
          <figure><img src="/assets/characters/vandal/idle/previews/vandal_idle_preview.gif" alt="Animated Vandal idle preview from the production frame set" /><figcaption><strong>Vandal · live idle pass</strong><span>Eight cleaned frames running at authored timing</span></figcaption></figure>
          <div><p className="foundry-kicker">CHROMA TO CHARACTER</p><h3>One pose becomes a verifiable runtime action.</h3><ol><li><b>01</b><span><strong>Isolate</strong> the approved pose from source art.</span></li><li><b>02</b><span><strong>Normalize</strong> transparency, bounds, pivots, and naming.</span></li><li><b>03</b><span><strong>Sequence</strong> the motion in a labeled contact sheet.</span></li><li><b>04</b><span><strong>Compile</strong> SFF/AIR data and exercise it in Ikemen.</span></li><li><b>05</b><span><strong>Prove</strong> the exact release artifact at the QA bench.</span></li></ol></div>
        </div>
        <div className="foundry-production-floor__evidence">
          <figure><img src="/assets/characters/vandal/idle/master-stance.png" alt="Vandal transparent master stance used as production reference" /><figcaption><strong>Master stance</strong><span>Transparent source and silhouette policy</span></figcaption></figure>
          <figure><img src="/assets/characters/vandal/idle/sheets/vandal_idle_grid.png" alt="Vandal labeled idle animation grid" /><figcaption><strong>Contact sheet</strong><span>Frame order, spacing, and visual QA</span></figcaption></figure>
          <figure><img src="/assets/characters/wake/idle/sheets/wake_idle_sheet.png" alt="Wake idle sprite sheet from the current character pipeline" /><figcaption><strong>Second fighter pass</strong><span>Shared harness, character-owned motion</span></figcaption></figure>
          <figure><img src="/assets/foundry/ikemen-integration-lab.png" alt="Fighter Foundry Ikemen integration laboratory" /><figcaption><strong>Runtime integration</strong><span>States, collision, effects, and stage checks</span></figcaption></figure>
          <figure><img src="/assets/foundry/qa-release-bench.png" alt="Fighter Foundry QA and release bench" /><figcaption><strong>Release evidence</strong><span>Package lineage and playable verification</span></figcaption></figure>
        </div>
      </section>

      <section className="foundry-ledger" aria-labelledby="frame-ledger-title">
        <div className="foundry-section-heading">
          <p>Measured production depth</p>
          <h2 id="frame-ledger-title">Compiled versus active fighter frames</h2>
          <span>Compiled drawings passed asset validation. Active drawings are reachable from current AIR actions and state data. The gap is visible work, not hidden polish.</span>
        </div>
        <div className="foundry-chart" role="img" aria-label="Bar chart comparing compiled and active frame counts for seven fighters">
          {frameRows.map(row => (
            <div className="foundry-chart__row" key={row.name}>
              <strong>{row.name}</strong>
              <div>
                <i className="compiled" style={{ width: `${row.compiled / maximum * 100}%` }}><span>{row.compiled}</span></i>
                <i className="active" style={{ width: `${row.active / maximum * 100}%` }}><span>{row.active}</span></i>
              </div>
            </div>
          ))}
          <div className="foundry-chart__legend"><span><i className="compiled" /> Compiled</span><span><i className="active" /> Active</span></div>
        </div>
        <button className="foundry-text-action" type="button" onClick={() => navigate('smackers-progress')}>Open the progress ledger →</button>
      </section>

      <section className="foundry-gallery" aria-label="Fighter Foundry production evidence">
        {[
          ['/assets/characters/candy/action-960.webp', 'Candy', '312 compiled · 307 active'],
          ['/assets/characters/beastly/action-960.webp', 'Beastly', '401 compiled · 381 active'],
          ['/assets/characters/shiv/action-960.webp', 'Shiv', '392 compiled · 372 active'],
          ['/assets/attract/story/convergence/08-smackers-unite.webp', 'The Convergence', 'Authored browser story sequence'],
        ].map(([src, title, caption]) => (
          <figure key={title}>
            <img src={src} alt={`${title} production artwork`} />
            <figcaption><strong>{title}</strong><span>{caption}</span></figcaption>
          </figure>
        ))}
      </section>

      <section className="foundry-release-band">
        <div>
          <p className="foundry-kicker">TESTER RELEASE</p>
          <h2>The current executable is visible, but not public.</h2>
          <p>The test package follows the build exercised at <code>The-Smackers-Game/SmackdownSupreme.exe</code>. L34D sign-in and private GitHub access restrict retrieval to accepted collaborators and team members.</p>
        </div>
        <div className="foundry-release-band__actions">
          <a href={MEMBER_DOWNLOAD_URL}>Get tester build</a>
          <button type="button" onClick={() => navigate('release-access')}>How access works</button>
          <a href={DOWNLOAD_URL} target="_blank" rel="noreferrer">Release anchor</a>
        </div>
      </section>
    </>
  );
}

function DetailPage({
  page,
  navigate,
  onLaunchDemo,
}: {
  page: NonNullable<ReturnType<typeof pageBySlug>>;
  navigate: (slug?: string) => void;
  onLaunchDemo: () => void;
}) {
  const currentIndex = foundryPages.findIndex(item => item.slug === page.slug);
  const nextPage = foundryPages[(currentIndex + 1) % foundryPages.length];
  return (
    <article className="foundry-detail">
      <header className="foundry-detail__hero">
        <div>
          <button className="foundry-breadcrumb" type="button" onClick={() => navigate()}>← Foundry field notes</button>
          <p className="foundry-kicker">{page.number} · {page.eyebrow}</p>
          <h1>{page.title}</h1>
          <p>{page.summary}</p>
        </div>
        <figure><img src={page.image} alt={page.imageAlt} /></figure>
      </header>

      <dl className="foundry-detail__facts">
        {page.facts.map(([value, label]) => <div key={label}><dt>{value}</dt><dd>{label}</dd></div>)}
      </dl>

      {page.slug === 'smackers-progress' && <Suspense fallback={<div className="foundry-progress-loading" role="status">AIMEE is powering the live progress floor…</div>}><FoundryProgressLab /></Suspense>}

      <div className="foundry-detail__body">
        <aside>
          <strong>On this page</strong>
          {page.sections.map((section, index) => <a key={section.title} href={`#section-${index + 1}`}>{String(index + 1).padStart(2, '0')} {section.title}</a>)}
          {page.slug === 'release-access' && <a href={MEMBER_DOWNLOAD_URL}>Tester build ↗</a>}
        </aside>
        <div className="foundry-detail__sections">
          {page.sections.map((section, index) => (
            <section id={`section-${index + 1}`} key={section.title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h2>{section.title}</h2>
              <p>{section.body}</p>
              {section.bullets && <ul>{section.bullets.map(item => <li key={item}>{item}</li>)}</ul>}
              {section.code && <pre><code>{section.code}</code></pre>}
              {section.note && <p className="foundry-detail__note">{section.note}</p>}
            </section>
          ))}
          {page.slug === 'visual-qa' && <ContactSheetStrip />}
          {page.slug === 'smackers-progress' && <ProgressChart />}
        </div>
      </div>

      <section className="foundry-next">
        <div><span>Next field note</span><strong>{nextPage.title}</strong></div>
        <button type="button" onClick={() => navigate(nextPage.slug)}>Open {nextPage.number} →</button>
        <button type="button" onClick={onLaunchDemo}>Launch demo</button>
      </section>
    </article>
  );
}

function ContactSheetStrip() {
  return (
    <section className="foundry-contact-strip" aria-labelledby="contact-strip-title">
      <span>04</span><h2 id="contact-strip-title">Review surfaces</h2>
      <div>{['candy', 'vandal', 'absolution', 'dj'].map(name => <img key={name} src={`/assets/characters/${name}/action-960.webp`} alt={`${name} action artwork used during visual review`} />)}</div>
    </section>
  );
}

function ProgressChart() {
  const maximum = maxFrameCount();
  return (
    <section className="foundry-inline-chart" aria-labelledby="inline-chart-title">
      <span>04</span><h2 id="inline-chart-title">Current frame ledger</h2>
      {frameRows.map(row => (
        <div key={row.name}>
          <strong>{row.name}</strong>
          <i style={{ width: `${row.compiled / maximum * 100}%` }}><b style={{ width: `${row.active / row.compiled * 100}%` }} /></i>
          <small>{row.active} / {row.compiled}</small>
        </div>
      ))}
    </section>
  );
}
